// SEO service config (Post a Project, Atrium Stage 7).
//
// Source of truth = the Rails backend, NOT the design prototype:
//   whitelist  app/controllers/api/v1/project_postings_controller.rb (seo_posting_params)
//   views      app/views/job_postings/steps/seo/_one.._four.html.erb
//   validation app/models/job_postings/job_postings/step_validations.rb (seo__one.._four)
//   cost       app/models/job_postings/seo_posting.rb (#cost / #editing_cost / #cost_per_article)
//   parity     tasks/post-a-project/parity/enhance-text.md §1
//
// Field keys are the snake_case backend attrs. The consent composite is expanded
// to ai_consent_* booleans by serialize.js, not here. The design owns the control:
// ONE optional "Competitor URLs" textarea (one URL per line). The backend keeps the
// three columns comp_url_1/2/3, so serialize.js splits the textarea's lines back
// into those params (the same composite-expansion idiom as consent/trigger). The
// backend accepts three params, not three inputs (tuw-app:P-002).

import {
  SEO_NICHES,
  WRITING_EDITING_STYLES,
  RATES,
} from "../options"

// string[] -> [{ value, label }] for select fields.
const asOptions = (arr) => arr.map((v) => ({ value: v, label: v }))

const money = (n) => Math.round(n * 100) / 100

// Design W_EDIT option descriptions (service-config.js). Em dash converted per
// the no-em-dash rule.
const WRITING_EDITING_DESCRIPTIONS = {
  "Essential Editing":
    "Grammar, spelling, syntax, punctuation and inconsistencies, included with every writing package.",
  "Line & Essential Editing":
    "Everything in Essential, plus a line-by-line review for clarity, flow, phrasing and sentence variety.",
}

// WRITING_EDITING_STYLES radio options with the design price text:
// Essential -> "Included", Line & Essential -> "$8.00 / 1,000 words".
// Server-traced via ctx.rates (ruling C2 / tuw-app:P-001), RATES seed as fallback.
const editingOptions = (ctx = {}) =>
  WRITING_EDITING_STYLES.map((style) => {
    // Per-key server-rate read: a null server leaf falls back to the RATES seed
    // for THIS style, never to 0 (tuw-app:P-001). Both styles are always in the
    // seed (Essential 0 / Line & Essential 0.8), so no trailing || 0 is needed.
    const rate = ctx.rates?.writing_editing_styles_cost?.[style] ?? RATES.WRITING_EDITING_STYLES_COST[style]
    return {
      value: style,
      label: style,
      price: rate > 0 ? `$${(rate * 10).toFixed(2)} / 1,000 words` : "Included",
      sub: WRITING_EDITING_DESCRIPTIONS[style],
    }
  })

// Per-article SEO rate for the current article count, bucketed exactly as
// seo_posting.rb#cost_per_article. Reads the server-resolved ctx.rates.seo_cost
// (Insider-aware) with RATES.SEO_COST as the no-live-PriceList fallback.
const seoRateFor = (articleCount, ctx = {}) => {
  const count = Number(articleCount) || 0

  let bucket
  if (count >= 30) bucket = 30
  else if (count >= 20) bucket = 20
  else if (count >= 15) bucket = 15
  else if (count >= 10) bucket = 10
  else if (count >= 5) bucket = 5
  else bucket = 0

  // Per-key: a null server leaf for this bucket falls back to the RATES seed
  // bucket, never to undefined (tuw-app:P-001). Every bucket key is in the seed.
  return ctx.rates?.seo_cost?.[bucket] ?? RATES.SEO_COST[bucket]
}

export const seo = {
  key: "seo",
  postingKey: "seo_posting",
  name: "SEO Content",

  steps: () => [
    // Step 1 — Article count (seo__one).
    {
      id: "article_count",
      title: "How many articles?",
      sub: "Each article is written and SEO-optimized by a copywriter + copyeditor.",
      fields: [
        {
          type: "count",
          key: "article_count",
          label: "Articles",
          required: true,
          min: 1,
          max: 100,
          step: 1,
          unit: "articles",
        },
      ],
    },

    // Step 2 — Article brief (seo__two). These fields pre-populate every article.
    {
      id: "brief",
      title: "Article brief",
      sub: "These fields pre-populate every article, you can fine-tune each one after posting.",
      fields: [
        {
          type: "wordcount",
          key: "word_count",
          label: "Words per article",
          help: "Minimum 500, in increments of 100.",
          required: true,
          min: 500,
          step: 100,
        },
        {
          type: "select",
          key: "niche",
          label: "Niche / genre",
          required: true,
          blankLabel: "Select Niche/Genre",
          options: asOptions([...SEO_NICHES].sort()),
        },
        { type: "text", key: "website_url", label: "Website URL", required: true, placeholder: "https://" },
        { type: "text", key: "main_keyword", label: "Main keyword", required: true },
        {
          type: "textarea",
          key: "other_keywords",
          label: "Other keywords",
          required: false,
          rows: 3,
        },
        { type: "text", key: "tone", label: "Tone", required: false },
        {
          type: "text",
          key: "target_audience",
          label: "Target audience",
          required: false,
        },
        {
          // Design owns the control (one optional textarea, one URL per line);
          // the backend owns the field set (comp_url_1/2/3). `split` tells
          // serialize.js to fan the textarea's lines out into those three params
          // (tuw-app:P-002). Storage caps at three, so extra lines are dropped.
          type: "textarea",
          key: "comp_urls",
          label: "Competitor URLs",
          required: false,
          rows: 3,
          placeholder: "One per line",
          split: { keys: ["comp_url_1", "comp_url_2", "comp_url_3"] },
        },
        {
          type: "toggle",
          key: "same_title_header",
          label: "Use the title as the header",
        },
        { type: "toggle", key: "faq_section", label: "Include an FAQ section" },
        { type: "toggle", key: "images", label: "Include images" },
        { type: "toggle", key: "videos", label: "Include videos" },
        {
          type: "textarea",
          key: "external_links",
          label: "Requirements & internal/external links",
          required: false,
          rows: 3,
        },
        {
          type: "file",
          key: "file",
          label: "Requirements file",
          required: false,
          accept: ".doc,.docx,.pdf,.zip",
          placeholder: "Upload a file",
        },
      ],
    },

    // Step 3 — Editing style (seo__three). Editing is included with the package;
    // the customer picks Essential (included) or Line & Essential ($8/1000 words).
    {
      id: "editing",
      title: "Editing style",
      sub: "Editing is included. Pick the level for your articles.",
      fields: [
        {
          type: "radio",
          key: "editing_style",
          required: true,
          options: (v = {}, ctx = {}) => editingOptions(ctx),
        },
      ],
    },

    // Step 4 — AI use (seo__four -> _ai_consent). 3-option consent (none / research
    // / all); the "all" option shows the 7-bullet permission list.
    {
      id: "ai",
      title: "AI use in your project",
      sub: "Choose how much your writer may use AI. Please select one.",
      fields: [
        {
          type: "consent",
          key: "ai_consent",
          mode: "3",
          label: "AI use in your project",
          required: true,
        },
      ],
    },
  ],

  // Instant estimate for the PriceRail. Ruby (seo_posting.rb#cost):
  //   (word_count / 100.0) * cost_per_article * article_count
  //   + editing_cost * article_count   (editing_cost = (word_count/100) * rate)
  // word_count is stepped at 100 so float and integer division coincide.
  // cost_per_article = SEO_RATE_FOR(article_count); rate = 0.8 (Line) or 0 (Essential).
  estimate: (values = {}, ctx = {}) => {
    const wc = Number(values.word_count) || 0
    const ac = Number(values.article_count) || 0
    const lines = []

    if (wc > 0 && ac > 0) {
      const perArticle = seoRateFor(ac, ctx)
      const base = (wc / 100) * perArticle * ac
      lines.push({
        label: `SEO (${ac} ${ac === 1 ? "article" : "articles"}, ${wc.toLocaleString()} words each)`,
        amount: money(base),
      })

      const editRate = ctx.rates?.writing_editing_styles_cost?.[values.editing_style] ?? RATES.WRITING_EDITING_STYLES_COST[values.editing_style] ?? 0
      const editAdd = (wc / 100) * editRate * ac

      if (editAdd > 0) {
        lines.push({ label: "Line editing upgrade", amount: money(editAdd) })
      }
    }

    const total = money(lines.reduce((sum, l) => sum + l.amount, 0))

    return { lines, total, isRange: false, framing: "free-to-post" }
  },

  // /post/price entry (camelCase; underscored server-side). cost_fields for
  // seo_posting = [word_count, article_count, editing_style, quality_assurance,
  // quality_control]. QA/QC are JOB-LEVEL singletons (state.quality_assurance_addon
  // / quality_control_enabled) not visible to (values, ctx); the engine layers them
  // onto this eligible service's payload. Null until word + article counts are set.
  pricePayload: (values = {}) => {
    const wc = Number(values.word_count) || 0
    const ac = Number(values.article_count) || 0
    if (wc <= 0 || ac <= 0) return null

    return {
      postingType: "seo_posting",
      wordCount: wc,
      articleCount: ac,
      editingStyle: values.editing_style ?? null,
    }
  },
}
